import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';
import { Home } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { ResellerCampaign, getResellerCampaigns } from '../../services/campaign-service';

const formatDate = (dateString: string) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const CampaignsPage: React.FC = () => {
  const { setBreadcrumbs } = useBreadcrumb();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<ResellerCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    setBreadcrumbs([
      { label: 'Home', path: '/', icon: <Home sx={{ fontSize: 16 }} /> },
      { label: 'Campanhas' },
    ]);
  }, [setBreadcrumbs]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const response = await getResellerCampaigns(page * rowsPerPage, rowsPerPage);

        if (response && Array.isArray(response.items)) {
          setCampaigns(response.items);
          setTotalItems(response.total);
        } else {
          enqueueSnackbar('Falha ao carregar campanhas', { variant: 'error' });
          setCampaigns([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error('Error fetching campaigns:', error);
        enqueueSnackbar('Falha ao carregar campanhas', { variant: 'error' });
        setCampaigns([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    };

    fetchCampaigns();
  }, [page, rowsPerPage, enqueueSnackbar]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Descrição</TableCell>
                  <TableCell>Público Alvo</TableCell>
                  <TableCell align="center">Chats iniciados</TableCell>
                  <TableCell align="center">Leads</TableCell>
                  <TableCell align="center">Solicitações de orçamento</TableCell>
                  <TableCell align="right">Criada em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {campaigns.length > 0 ? (
                  campaigns.map(campaign => (
                    <TableRow
                      key={campaign.id}
                      hover
                      onClick={() => navigate(`/campaigns/${campaign.id}`)}
                      sx={{ cursor: 'pointer' }}
                    >
                      <TableCell>{campaign.title}</TableCell>
                      <TableCell>{campaign.description}</TableCell>
                      <TableCell>{campaign.target_audience}</TableCell>
                      <TableCell align="center">{campaign.started_chats}</TableCell>
                      <TableCell align="center">{campaign.leads_count}</TableCell>
                      <TableCell align="center">{campaign.requested_quotes}</TableCell>
                      <TableCell align="right">{formatDate(campaign.created_at)}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Nenhuma campanha encontrada
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="Linhas por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </>
      )}
    </Box>
  );
};

export default CampaignsPage;
